import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { DividendPaymentDataDto } from 'api/Trading-Accounts/Types/DividendsDto'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { format } from 'date-fns'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchInstrumentsAction } from 'store/trading-accounts/trading-accounts.actions'
import {
  fetchDirectionType,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { getIdFromLocationHeader } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { defaultDateFormat } from 'utils/date'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, FilterLogic } from 'utils/QueryHelpers'

import { TransactionDetailsView } from '../../Details/ResolveTransactionDetailsRoute'
import { ImportTransactionPage } from '../ImportTransaction'
import { TransactionUploadHistoryPage } from '../TransactionUploadHistoryPage'
import {
  AdjustmentTradingAccountForm,
  AdjustmentTradingAccountFormValues,
} from './AdjustmentTradingAccountForm'

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto

  setClient(): void
}

export const AdjustmentTradingAccountPage: React.FC<Props> = ({ client, setClient }) => {
  const [auth] = useContext(AuthSessionContext)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const setToast = useContext(ToastContext)

  const transactionTypes = useAppSelector((state) => state.transactions.types)
  const directionTypes = useAppSelector((state) => state.transactions.directionType)
  const brokerSymbols = useAppSelector((state) => state.tradingAccounts.instruments)

  const dateFormatType = auth?.user.dateFormatType.name || defaultDateFormat

  const [importModal, setImportModal] = useState(false)
  const [historyModal, setHistoryModal] = useState<{
    visible: boolean
    uploadFileHistoryId?: string
  }>({
    visible: false,
  })

  const [confirmationData, setConfirmationData] =
    useState<AdjustmentTradingAccountFormValues | null>(null)

  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)
  const [tradingAccountId, setTradingAccountId] = useState('')
  const [symbolData, setSymbolData] = useState<InstrumentDto | undefined>()

  const dispatch = useAppDispatch()

  const { callback: fetchTAData, forceRefresh: forceTARefresh } =
    useCallbackWithForceRefresh(async () => {
      if (!client) {
        return undefined
      }
      const response = await tradingAccountsApiClient.getTradingAccountByClientId(client.id, {
        pageIndex: 1,
        pageSize: 100,
      })
      return response.items
    }, [client])
  const { data: tradingAccounts = [], isLoading } = useFetchOne(fetchTAData)

  const fetchDividendsData = useCallback(async () => {
    if (symbolData?.id) {
      const response = await tradingAccountsApiClient.getDividends({
        dividendPaymentDataType: 1,
        search: buildQuery({
          filters: [
            {
              field: 'Symbol',
              operator: '==',
              value: symbolData.symbol,
            },
            {
              field: 'ExDate',
              operator: '>=',
              value: getPreviousMonthDate(),
            },
          ],
          logic: FilterLogic.AND,
        }),
        pageIndex: 1,
        pageSize: 20,
        skipCount: true,
      })
      return response.items
    }
  }, [symbolData]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: dividends = [] } = useFetchOne(fetchDividendsData)

  const handleSymbolChange = (instruments: InstrumentDto) => {
    setSymbolData(instruments)
  }

  useEffect(() => {
    dispatch(fetchTransactionTypeAction(walletsApiClient, { tags: 'TradingAccountAdjustments' }))
    if (isEmpty(directionTypes)) dispatch(fetchDirectionType(walletsApiClient))
    if (isEmpty(brokerSymbols)) dispatch(fetchInstrumentsAction(tradingAccountsApiClient))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: AdjustmentTradingAccountFormValues) => {
    setConfirmationData(values)
  }

  const onImportSubmit = (uploadFileHistoryId: string) => {
    setImportModal(false)
    setHistoryModal({ visible: true, uploadFileHistoryId })
  }

  const setTradingAccount = async (taId: string) => {
    const tradingAccount = (tradingAccounts || []).find((x) => x.id === taId)
    if (tradingAccount) {
      setTradingAccountId(tradingAccount.id)
      setCurrency(tradingAccount.currency.id)
    }
  }

  const onSetClient = () => {
    setClient()
  }

  const handleConfirmationSubmit = async () => {
    if (confirmationData) {
      try {
        const dividendDataName = getDividendDataName(dividends, confirmationData, dateFormatType)
        const data = {
          ...confirmationData,
          comment:
            Number(confirmationData.transactionTypeId) === TransactionDetailsView.DividendAdjustment
              ? `${dividendDataName}\n${confirmationData.comment}`
              : (confirmationData.comment ?? ''),
        }
        const response = await walletsApiClient.createTradingAccountAdjustment(data)
        setToast({
          type: 'success',
          requireInteraction: true,
          title: `Transaction has been submitted.`,
          subtitle: 'Click for details',
          url: generatePath('/transactions/:id' as string, {
            id: getIdFromLocationHeader(response.headers.location),
          }),
        })
        forceTARefresh()
        setConfirmationData(null)
      } catch (e) {
        console.error(e)
        setConfirmationData(null)
      }
    }
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationData(null)
  }

  const selectTradingAccountName = () => {
    const tradingAccountDataId = confirmationData?.tradingAccountId
    if (tradingAccountDataId) {
      return tradingAccounts?.find((x) => x?.id === tradingAccountDataId)?.name
    }
  }

  const selectTransactionTypeName = () => {
    const transactionTypeId = confirmationData?.transactionTypeId
    if (transactionTypeId) {
      return transactionTypes?.find((x) => x?.id === transactionTypeId)?.name
    }
  }

  const selectDirectionTypeName = () => {
    const directionTypeId = confirmationData?.directionTypeId
    if (directionTypeId) {
      return directionTypes?.find((x) => x?.id === Number.parseInt(directionTypeId, 10))?.name
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {historyModal.visible && historyModal.uploadFileHistoryId && (
        <Modal
          width={1200}
          margin='200'
          visible={historyModal.visible}
          onClose={() => setHistoryModal({ visible: false, uploadFileHistoryId: undefined })}
        >
          <TransactionUploadHistoryPage
            onClose={() => setHistoryModal({ visible: false, uploadFileHistoryId: undefined })}
            historyUploadId={historyModal.uploadFileHistoryId || ''}
          />
        </Modal>
      )}
      {importModal && (
        <Modal visible={importModal} onClose={() => setImportModal(false)}>
          <ImportTransactionPage
            onClose={() => setImportModal(false)}
            onSubmit={onImportSubmit}
            setToast={setToast}
          />
        </Modal>
      )}
      {!!confirmationData && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={!!confirmationData}
          renderTitle={
            <div>
              <p>
                You are going to do an adjustment for the trading account{' '}
                {selectTradingAccountName()}. The parameters selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {client?.name}
                </div>
                <div id='field'>
                  <strong>Trading Account:</strong> {selectTradingAccountName()}
                </div>
                <div id='field'>
                  <strong>Transaction Type:</strong> {selectTransactionTypeName()}
                </div>
                <div id='field'>
                  <strong>Direction Type:</strong> {selectDirectionTypeName()}
                </div>
                <div id='field'>
                  <strong>Amount:</strong> {confirmationData?.amount} {currency}
                </div>
                {confirmationData?.transactionTypeId ===
                  TransactionDetailsView.DividendAdjustment && (
                  <div id='field'>
                    <strong>Dividend Data:</strong>{' '}
                    {getDividendDataName(dividends, confirmationData, dateFormatType)}
                  </div>
                )}
              </div>
            </div>
          }
        />
      )}
      <AdjustmentTradingAccountForm
        directionTypes={directionTypes}
        transactionTypes={transactionTypes}
        setTradingAccount={setTradingAccount}
        tradingAccountId={tradingAccountId}
        instruments={brokerSymbols}
        dividends={dividends}
        currency={currency}
        setCurrency={setCurrency}
        client={client}
        setClient={onSetClient}
        tradingAccounts={tradingAccounts}
        permissions={auth?.permissions || []}
        dateFormatType={dateFormatType}
        onSubmit={onSubmit}
        onSymbolChange={handleSymbolChange}
        showImportTransactionModal={() => setImportModal(true)}
      />
    </React.Fragment>
  )
}

const getPreviousMonthDate = () => {
  const date = new Date()
  date.setMonth(date.getMonth() - 1)
  return date
}

const getDividendDataName = (
  dividends: DividendPaymentDataDto[] = [],
  data: AdjustmentTradingAccountFormValues,
  dateFormatType: string
) => {
  const dividendPaymentData = dividends.find((x) => x.id === data.dividendPaymentDataId)
  return (
    dividendPaymentData &&
    `${dividendPaymentData.exchangeSymbol}, ${
      dividendPaymentData.eventSubType.name
    }, ExDate=${format(new Date(dividendPaymentData.eventDate), dateFormatType)}, Rate=${
      dividendPaymentData.amount
    } ${dividendPaymentData.currency.id}`
  )
}
